import { useRef, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Pagination from "../components/pagination/Pagination";
import { Outlet, useMatch } from "react-router-dom";
import {
  getUsers,
  getSearchUsers,
  getImageUrl,
  uploadPalm,
  updatePalm,
} from "../utils/http";
import UserList from "../components/users/user-list";
import SearchBar from "../components/search/search";
import RoundedButton from "../components/buttons/RoundedButton";
import Modal from "../components/modal/modal";
import Loader from "../components/loader/loader";
import { IoIosClose } from "react-icons/io";
import { showSuccessToast } from "../utils/utils";

export default function PalmData() {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [userName, setUserName] = useState("");
  const [userMobile, setUserMobile] = useState("");
  const modalRef = useRef();
  const [isOpen, setIsOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);

  const [productImages, setProductImages] = useState([]);
  const queryClient = useQueryClient();

  const name = useRef();
  const mobile = useRef();
  const dob = useRef();
  const placeOfBirth = useRef();
  const timeOfBirth = useRef();
  const prediction = useRef();

  const openModal = (item = null) => {
    if (item) {
      setIsEditMode(true);
      setEditingUserId(item._id);
      name.current.value = item.name || "";
      mobile.current.value = item.mobile_number || "";
      dob.current.value = item.dob || "";
      placeOfBirth.current.value = item.place_of_birth || "";
      timeOfBirth.current.value = item.time_of_birth || "";
      prediction.current.value = item.prediction || "";
      setProductImages(item.images || []);
    } else {
      setIsEditMode(false);
      setEditingUserId(null);
      name.current.value = "";
      mobile.current.value = "";
      dob.current.value = "";
      placeOfBirth.current.value = "";
      timeOfBirth.current.value = "";
      prediction.current.value = "";
      setProductImages([]);
    }
    modalRef.current.open();
  };

  const closeModal = () => {
    modalRef.current.close();
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: ["users", currentPage, searchTerm],
    queryFn: () => {
      if (searchTerm) {
        return getSearchUsers({
          searchTerm: searchTerm,
          page: currentPage,
        });
      }
      return getUsers({ page: currentPage });
    },
  });

  useEffect(() => {
    if (data) {
      setTotalUsers(data.totalUsers);
    }
  }, [data]);
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  const searchUser = (keyword) => {
    setSearchTerm(keyword);
    setCurrentPage(1);
  };
  const handleClearUser = () => {
    setSearchTerm("");
    setCurrentPage(1);
  };

  function handleClick(item) {
    openModal(item.item);
  }
  const handleImageChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        mutate({
          image: file,
          folder: "palm",
        });
        // setImageSrc(reader.result);
      };
      reader.readAsDataURL(file);
      //   setImageFile(file);
    }
  };
  function removeImage(index) {
    setProductImages((prevState) => {
      return prevState.filter((item, i) => i !== index);
    });
  }
  const { mutate } = useMutation({
    mutationFn: getImageUrl,
    onSuccess: (result) => {
      if (result && result.includes("https") && result.endsWith(".jpg")) {
        setProductImages((prevState) => {
          return [...prevState, result];
        });
      }
    },
  });
  const { mutate: uploadPalmData, isPending } = useMutation({
    mutationFn: uploadPalm,
    onSuccess: (result) => {
      showSuccessToast("Uploaded");
      queryClient.invalidateQueries("users");
      closeModal();
      name.current.value = "";
      mobile.current.value = "";
      placeOfBirth.current.value = "";
      prediction.current.value = "";
      timeOfBirth.current.value = "";
      dob.current.value = "";
    },
  });
  const { mutate: updatePalmData, isPending: isUpdating } = useMutation({
    mutationFn: updatePalm,
    onSuccess: (result) => {
      showSuccessToast("Updated");
      queryClient.invalidateQueries("users");
      closeModal();
      name.current.value = "";
      mobile.current.value = "";
      placeOfBirth.current.value = "";
      prediction.current.value = "";
      timeOfBirth.current.value = "";
      dob.current.value = "";
      setIsEditMode(false);
      setEditingUserId(null);
    },
  });
  function handleUploadPalm(event) {
    event.preventDefault();
    const payload = {
      name: name.current.value,
      dob: dob.current.value,
      place_of_birth: placeOfBirth.current.value,
      time_of_birth: timeOfBirth.current.value,
      mobile: mobile.current.value,
      created_by: "Harsh",
      prediction: prediction.current.value,
      images: productImages,
    };
    if (isEditMode && editingUserId) {
      updatePalmData({ ...payload, id: editingUserId, update: true });
    } else {
      uploadPalmData(payload);
    }
  }

  return (
    <>
      <Modal
        ref={modalRef}
        title={isEditMode ? "Edit Palm Details" : "Enter Palm Details"}
        onClose={closeModal}
      >
        <form className="flex flex-col items-start ">
          <label htmlFor="user-name" className="text-sm text-slate-400">
            Name
          </label>
          <input
            type="text"
            id="user-name"
            name="user-name"
            placeholder="enter name"
            required
            ref={name}
            className="border border-slate-400 px-2 py-2 mb-3 rounded-md focus:outline-none w-full"
          />
          <label htmlFor="user-mobile" className="text-sm text-slate-400">
            Mobile
          </label>
          <input
            type="text"
            id="user-mobile"
            name="user-mobile"
            placeholder="enter mobile"
            ref={mobile}
            className="border border-slate-400 px-2 py-2 mb-3 rounded-md focus:outline-none w-full"
          />
          <label htmlFor="user-dob" className="text-sm text-slate-400">
            DOB
          </label>
          <input
            type="text"
            id="user-dob"
            name="user-dob"
            placeholder="enter dob"
            ref={dob}
            className="border border-slate-400 px-2 py-2 mb-3 rounded-md focus:outline-none w-full"
          />
          <label
            htmlFor="user-place-of-birth"
            className="text-sm text-slate-400"
          >
            Place of Birth
          </label>
          <input
            type="text"
            id="user-place-of-birth"
            name="user-place-of-birth"
            placeholder="enter place of birth"
            ref={placeOfBirth}
            className="border border-slate-400 px-2 py-2 mb-3 rounded-md focus:outline-none w-full"
          />
          <label
            htmlFor="user-time-of-birth"
            className="text-sm text-slate-400"
          >
            Time of Birth
          </label>
          <input
            type="text"
            id="user-time-of-birth"
            name="user-time-of-birth"
            placeholder="enter time of birth"
            ref={timeOfBirth}
            className="border border-slate-400 px-2 py-2 mb-3 rounded-md focus:outline-none w-full"
          />

          <label htmlFor="prediction" className="text-sm text-slate-400">
            Prediction
          </label>
          <textarea
            type="text"
            id="prediction"
            name="prediction"
            placeholder="write prediction"
            ref={prediction}
            rows={10}
            className="border text-sm border-slate-400 px-2 py-2 mb-3 rounded-md focus:outline-none w-full  resize-none"
          />
          <label htmlFor="product-image" className="text-sm text-slate-400">
            Add Product Images
          </label>
          <input
            type="file"
            accept="image/*"
            id="product-image"
            name="product-image"
            onChange={handleImageChange}
          />
          <div className="flex gap-3 mt-3  overflow-x-auto w-full">
            {productImages.map((item, index) => {
              return (
                <div
                  id={item}
                  className="w-52 h-52 flex-shrink-0 border border-gray-300 relative mb-5"
                >
                  <img
                    src={item}
                    className="h-52 w-52 object-contain"
                    alt="product-image"
                  />
                  <IoIosClose
                    className="text-4xl absolute top-2 right-2"
                    onClick={() => removeImage(index)}
                  />
                </div>
              );
            })}
          </div>

          {isPending || isUpdating ? (
            <Loader />
          ) : (
            <RoundedButton
              onClick={handleUploadPalm}
              text={isEditMode ? "Update" : "Upload"}
            />
          )}
        </form>
      </Modal>
      <div className="h-full flex flex-col">
        <div className="flex justify-start items-center gap-3 mb-2">
          <h1 className="text-2xl">Users</h1>
          <RoundedButton text="Add" onClick={() => openModal()} />
        </div>
        <div className="flex-grow">
          <SearchBar onSearch={searchUser} onClear={handleClearUser} />
          <div className="mt-4">
            {isLoading ? (
              <p>Loading...</p>
            ) : isError ? (
              <p>Something went wrong</p>
            ) : (
              <UserList users={data?.data || []} onClick={handleClick} />
            )}
          </div>
        </div>

        <Pagination
          totalProducts={totalUsers}
          productsPerPage={50}
          onPageChange={handlePageChange}
        />
        <Outlet />
      </div>
    </>
  );
}
